import type identity from "lodash/identity";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useMediaQueries } from "../../util/util";

const PaginationWrapper = styled.div`
  width: 100%;
`;
type PaginationButtonProps = {
  miniPagination: boolean;
};
const PaginationButton = styled.button<PaginationButtonProps>`
  padding: ${({ theme, miniPagination }) =>
    miniPagination ? "5px 7px" : `${theme.resolve("spacing_md")} 10px`};
  margin: ${({ theme, miniPagination }) =>
    miniPagination
      ? `10px ${theme.resolve("spacing_xs")}`
      : `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xs")}`};
  cursor: pointer;
  background: ${({ theme }) => theme.tertiaryButtonBG};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  color: ${({ theme }) => theme.primaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  min-width: ${({ miniPagination }) => (miniPagination ? "30px" : "35px")};
  &:disabled,
  &:hover {
    background: ${({ theme }) => theme.secondaryBG};
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.secondaryBG};
    color: ${({ theme }) => theme.primaryTextColor};
  }
  &:disabled {
    cursor: default;
  }
`;
const PageBtnWrapper = styled.div`
  display: inline;
  &:first-child ${PaginationButton} {
    margin-left: 0;
  }
`;

export interface IPaginationProps {
  pagination: {
    pageCount: number;
    pageIndex: number;
    perPage: number;
  };
  offset: number;
  handlePageClick: (offset: number) => void;
  t?: (val: string) => string | typeof identity;
  miniPagination?: boolean;
}

export const Pagination = (props: IPaginationProps) => {
  const { perPage, pageIndex, pageCount } = props.pagination;
  const { offset } = props;
  const handlePageClick = props.handlePageClick;
  const { t } = useTranslation();
  const { isMediumScreen } = useMediaQueries();

  const filterPages = useCallback(
    (visiblePages: Array<number>) => {
      return visiblePages.filter((page) => page <= pageCount);
    },
    [pageCount]
  );

  const getVisiblePages = useMemo(() => {
    if (pageCount < 7) {
      return filterPages([1, 2, 3, 4, 5, 6]);
    } else {
      if (pageIndex % 5 >= 0 && pageIndex > 4 && pageIndex + 2 < pageCount) {
        return [
          1,
          pageIndex - 2,
          pageIndex - 1,
          pageIndex,
          pageIndex + 1,
          pageIndex + 2,
          pageCount,
        ];
      } else if (
        pageIndex % 5 >= 0 &&
        pageIndex > 4 &&
        pageIndex + 2 >= pageCount
      ) {
        return [1, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
      } else {
        return [1, 2, 3, 4, 5, pageCount];
      }
    }
  }, [filterPages, pageCount, pageIndex]);

  const [visiblePages, setVisiblePages] = useState(getVisiblePages);

  const changePage = (page: number) => {
    const offset = (page - 1) * perPage || 0;
    handlePageClick(offset);
  };

  useEffect(() => {
    setVisiblePages(getVisiblePages);
  }, [getVisiblePages, offset, perPage]);

  const miniPagination = isMediumScreen || (props.miniPagination ?? false);

  return pageCount > 1 ? (
    <PaginationWrapper>
      {pageIndex > 1 && (
        <PaginationButton
          onClick={() => changePage(pageIndex - 1)}
          miniPagination={miniPagination}
        >
          {miniPagination ? " < " : t("Previous Page")}
        </PaginationButton>
      )}
      {visiblePages.map((page, index, array) => {
        return (
          <PageBtnWrapper key={page}>
            {array[index - 1] + 2 < page && ` ... `}
            <PaginationButton
              onClick={() => changePage(page)}
              disabled={page === pageIndex}
              miniPagination={miniPagination}
            >
              {page}
            </PaginationButton>
          </PageBtnWrapper>
        );
      })}
      {pageCount > pageIndex && (
        <PaginationButton
          onClick={() => changePage(pageIndex + 1)}
          miniPagination={miniPagination}
        >
          {miniPagination ? ">" : t("Next Page")}
        </PaginationButton>
      )}
    </PaginationWrapper>
  ) : (
    <></>
  );
};
